import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, FileText, Lock, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import Navigation from '@/components/Navigation';

const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

export default function TrustPage() {
  const principles = [
    { icon: Shield, title: "Creator-controlled permissions", desc: "Creators set their own boundaries. What's allowed, what's banned, how approvals work—all defined by the creator." },
    { icon: FileText, title: "No implied endorsements by default", desc: "Campaigns are presence-only unless the creator explicitly opts in to endorsement for that specific campaign." },
    { icon: Eye, title: "Clear licensing scope for every use", desc: "Every license defines term, territory, platforms, and restrictions. No ambiguity about what's permitted." },
    { icon: CheckCircle, title: "Receipts and verification links as proof", desc: "Every active license has a downloadable receipt and a shareable verification link that shows scope and status." },
    { icon: Lock, title: "Secure asset delivery with logged access", desc: "Assets are delivered through the platform. Downloads are logged for accountability and audit trails." },
    { icon: AlertTriangle, title: "Dispute reporting with evidence support", desc: "If something goes wrong, disputes can be reported with evidence and escalated through the platform." },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="trust-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-[800px] mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <div className="w-16 h-16 rounded-2xl bg-[#EEF2FA] flex items-center justify-center mx-auto mb-8">
              <Shield className="w-8 h-8 text-[#2563EB]" />
            </div>
            <h1 className="font-heading text-[44px] md:text-[56px] font-semibold text-[#101828] leading-tight mb-6">
              Trust, consent, and proof
            </h1>
            <p className="text-[#475467] text-lg leading-relaxed max-w-2xl mx-auto">
              This platform exists to make likeness licensing legitimate, safe, and verifiable 
              for both talent and agencies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 lg:py-28 section-surface">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="font-heading text-[32px] md:text-[40px] font-semibold text-[#101828] mb-4">
              Our principles
            </h2>
            <p className="text-[#475467] text-lg max-w-2xl mx-auto">
              Every feature we build is guided by these non-negotiable standards.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {principles.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="premium-card"
              >
                <div className="w-12 h-12 rounded-xl bg-[#EEF2FA] flex items-center justify-center mb-5">
                  <p.icon className="w-6 h-6 text-[#2563EB]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#101828] mb-2">{p.title}</h3>
                <p className="text-[#475467] text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What This Means */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[800px] mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-12">
            <h2 className="font-heading text-[32px] font-semibold text-[#101828] mb-4">
              What this means in practice
            </h2>
          </motion.div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-xl font-semibold text-[#101828] mb-3">For creators</h3>
              <ul className="space-y-3 text-[#475467]">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#12B76A] flex-shrink-0 mt-0.5" />
                  <span>Your full likeness pack is never shared until contracts are signed and payment is confirmed.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#12B76A] flex-shrink-0 mt-0.5" />
                  <span>You can see exactly what every license permits—term, territory, platforms, and restrictions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#12B76A] flex-shrink-0 mt-0.5" />
                  <span>If someone misuses your likeness, you have documentation to support your case.</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-xl font-semibold text-[#101828] mb-3">For agencies</h3>
              <ul className="space-y-3 text-[#475467]">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#12B76A] flex-shrink-0 mt-0.5" />
                  <span>You always know what you're licensed to do—no guessing, no ambiguity.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#12B76A] flex-shrink-0 mt-0.5" />
                  <span>Receipts and verification links give your procurement and legal teams confidence.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#12B76A] flex-shrink-0 mt-0.5" />
                  <span>Download logs prove you accessed assets within the license scope.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#D0D5DD] py-12 bg-[#F6F8FC]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-heading text-lg font-semibold text-[#101828]">Mylikeness</p>
            <p className="text-[#475467] text-sm">© 2026 Mylikeness. Licensed presence, verified proof.</p>
            <div className="flex gap-6 text-sm text-[#475467]">
              <Link to="/creators" className="hover:text-[#101828] transition-colors">For Creators</Link>
              <Link to="/agencies" className="hover:text-[#101828] transition-colors">For Agencies</Link>
              <a href="#" className="hover:text-[#101828] transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
